import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

export interface OnboardingStep {
  id: string;
  label: string;
  description: string;
  route: string;
  section: string;
}

/**
 * Onboarding steps for DataOps
 * Each step has:
 * - id: unique identifier
 * - label: display name
 * - description: what this step accomplishes
 * - route: where to go to complete this step
 * - section: grouping for visual organization
 */
export const DATAOPS_ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'explore_catalog',
    label: 'Explore Data Catalog',
    description: 'Browse your data assets and understand what data is available',
    route: '/dataops/catalog',
    section: 'discover'
  },
  {
    id: 'view_lineage',
    label: 'View Data Lineage',
    description: 'Understand how data flows through your pipelines',
    route: '/dataops/lineage',
    section: 'understand'
  },
  {
    id: 'monitor_runs',
    label: 'Monitor Pipeline Runs',
    description: 'Track your data pipeline executions and their status',
    route: '/dataops/runs',
    section: 'monitor'
  }
];

// Icon mapping for steps
export const DATAOPS_STEP_ICONS: Record<string, string> = {
  explore_catalog: 'FiDatabase',
  view_lineage: 'FiGitBranch',
  monitor_runs: 'FiPlay'
};

// Section labels
export const DATAOPS_SECTIONS: Record<string, string> = {
  discover: 'Discover',
  understand: 'Understand',
  monitor: 'Monitor'
};

interface DataOpsOnboardingStatus {
  hasExploredCatalog: boolean;
  hasViewedLineage: boolean;
  hasMonitoredRuns: boolean;
  loading: boolean;
  error: string | null;
}

type StepStatusMap = Record<string, boolean>;

interface UseDataOpsOnboardingStatusReturn extends DataOpsOnboardingStatus {
  stepStatusMap: StepStatusMap;
  completedSteps: number;
  totalSteps: number;
  completionPercentage: number;
  nextStep: OnboardingStep | undefined;
  isComplete: boolean;
  refresh: () => Promise<void>;
}

/**
 * Hook to track DataOps onboarding progress
 * Checks various APIs to determine which steps are complete
 */
export const useDataOpsOnboardingStatus = (): UseDataOpsOnboardingStatusReturn => {
  const { user } = useAuth();
  const [status, setStatus] = useState<DataOpsOnboardingStatus>({
    hasExploredCatalog: false,
    hasViewedLineage: false,
    hasMonitoredRuns: false,
    loading: true,
    error: null
  });

  const checkStatus = useCallback(async () => {
    if (!user) {
      setStatus(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      // For now, we'll use localStorage to track "viewed" states for pages
      // In a production app, this would be tracked server-side
      const hasExploredCatalog = localStorage.getItem('dataops_explored_catalog') === 'true';
      const hasViewedLineage = localStorage.getItem('dataops_viewed_lineage') === 'true';
      const hasMonitoredRuns = localStorage.getItem('dataops_monitored_runs') === 'true';

      setStatus({
        hasExploredCatalog,
        hasViewedLineage,
        hasMonitoredRuns,
        loading: false,
        error: null
      });
    } catch (err) {
      console.error('Error checking DataOps onboarding status:', err);
      setStatus(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : String(err)
      }));
    }
  }, [user]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // Calculate completion
  const completedSteps = [
    status.hasExploredCatalog,
    status.hasViewedLineage,
    status.hasMonitoredRuns
  ].filter(Boolean).length;

  const totalSteps = DATAOPS_ONBOARDING_STEPS.length;
  const completionPercentage = Math.round((completedSteps / totalSteps) * 100);

  // Map step IDs to their completion status
  const stepStatusMap: StepStatusMap = {
    explore_catalog: status.hasExploredCatalog,
    view_lineage: status.hasViewedLineage,
    monitor_runs: status.hasMonitoredRuns
  };

  // Find next incomplete step
  const nextStep = DATAOPS_ONBOARDING_STEPS.find(step => !stepStatusMap[step.id]);
  const isComplete = completedSteps === totalSteps;

  return {
    ...status,
    stepStatusMap,
    completedSteps,
    totalSteps,
    completionPercentage,
    nextStep,
    isComplete,
    refresh: checkStatus
  };
};

// Helper function to mark a step as complete (for page views)
export const markDataOpsStepComplete = (stepId: string): void => {
  const stepKeys: Record<string, string> = {
    explore_catalog: 'dataops_explored_catalog',
    view_lineage: 'dataops_viewed_lineage',
    monitor_runs: 'dataops_monitored_runs'
  };

  const key = stepKeys[stepId];
  if (key) {
    localStorage.setItem(key, 'true');
  }
};

export default useDataOpsOnboardingStatus;
